<!-- FieldSelector.vue -->
<template>
  <span>
    <a :class="['button', buttonClass || 'is-primary']" href="#_" v-on:click="activate">
      <slot></slot>
    </a>
    <b-modal
      v-model="active"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Select Fields</p>
          <button type="button" class="delete" v-on:click="close" />
        </header>
        <div class="modal-card-body">
          <!-- Value Editor -->
          <div class="field">
            <label class="label">Edit Value</label>
            <div class="control">
              <input
                type="text"
                class="input"
                v-model="editedValue"
                placeholder="Edit value before mapping..."
              />
            </div>
          </div>

          <!-- Search Box -->
          <div class="field">
            <label class="label">Save Info to Dossier</label>
            <div class="control">
              <input
                type="text"
                class="input"
                placeholder="Search fields to map to..."
                v-model="searchQuery"
              />
            </div>
          </div>

          <!-- List of Fields with Existing Values -->
          <div class="fields-list">
            <div
              v-for="field in filteredFields"
              :key="field.name"
              class="field-card"
              @click="selectField(field.name)"
              :class="{ 
                'is-selected': selectedField === field.name,
                'has-existing-value': field.currentValue
              }"
            >
              <div class="field-info">
                <div class="field-name">{{ field.name }}</div>
                <div v-if="field.currentValue" class="existing-value">
                  Current: {{ field.currentValue }}
                </div>
              </div>
            </div>

            <!-- Add Custom Field Button -->
            <div 
              class="field-card custom-field-button"
              @click="startCustomField"
              v-if="!isCreatingCustomField"
            >
              <div class="field-info">
                <div class="field-name">+ Add Custom Field</div>
              </div>
            </div>

            <!-- Custom Field Editor -->
            <div v-if="isCreatingCustomField" class="custom-field-editor">
              <div class="field">
                <label class="label">Field Name</label>
                <div class="control">
                  <input
                    type="text"
                    class="input"
                    v-model="customFieldName"
                    :placeholder="descriptiveKey"
                    ref="customFieldInput"
                  />
                </div>
              </div>
              <div class="field-buttons">
                <button 
                  class="button is-primary is-small"
                  @click="createCustomField"
                  :disabled="!customFieldName.trim()"
                >
                  Create Field
                </button>
                <button 
                  class="button is-light is-small"
                  @click="cancelCustomField"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-card-foot">
          <button 
            class="button is-primary" 
            v-on:click="submitSelection"
            :disabled="!isValidSelection"
          >
            Map to Selected Field
          </button>
          <button class="button" v-on:click="close">Cancel</button>
        </div>
      </div>
    </b-modal>
  </span>
</template>

<script>
const MOCK_DATA = {
  "First Name": "Gabe"
};

export default {
  props: {
    callback: Function,
    buttonClass: String,
    fieldValue: String,
    descriptiveKey: String,
    existingValues: {
      type: Object,
      default: () => MOCK_DATA
    }
  },
  data() {
    return {
      active: false,
      searchQuery: "",
      selectedField: null,
      editedValue: "",
      overwriteChoice: null,
      isCreatingCustomField: false,
      customFieldName: "",
      customFields: [], // Store dynamically added fields
    };
  },
  computed: {
    fields() {
      return [
        ...this.defaultFields,
        ...this.customFields
      ].map(fieldName => ({
        name: fieldName,
        currentValue: this.existingValues[fieldName] || null
      }));
    },
    defaultFields() {
      return [
        "First Name",
        "Last Name",
        "Middle Name",
        "Date of Birth"
      ];
    },
    filteredFields() {
      const query = this.searchQuery.toLowerCase();
      return this.fields.filter((field) =>
        field.name.toLowerCase().includes(query)
      );
    },
    isValidSelection() {
      const selectedFieldData = this.fields.find(f => f.name === this.selectedField);
      if (!selectedFieldData) return false;
      
      return this.selectedField && this.editedValue.trim() !== "";
    }
  },
  watch: {
    active(newValue) {
      if (newValue) {
        this.editedValue = this.fieldValue || "";
        this.customFieldName = "";
        this.isCreatingCustomField = false;
      }
    },
    selectedField(newValue) {
      this.overwriteChoice = null;
    },
    isCreatingCustomField(newValue) {
      if (newValue) {
        this.customFieldName = this.descriptiveKey;
        this.$nextTick(() => {
          this.$refs.customFieldInput?.focus();
        });
      }
    }
  },
  methods: {
    startCustomField() {
      this.isCreatingCustomField = true;
    },
    createCustomField() {
      const fieldName = this.customFieldName.trim();
      if (fieldName && !this.fields.some(f => f.name === fieldName)) {
        this.customFields.push(fieldName);
        this.selectField(fieldName);
        this.isCreatingCustomField = false;
      }
    },
    cancelCustomField() {
      this.isCreatingCustomField = false;
      this.customFieldName = "";
    },
    close() {
      this.active = false;
      this.resetForm();
    },
    activate() {
      this.active = true;
    },
    resetForm() {
      this.selectedField = null;
      this.editedValue = "";
      this.searchQuery = "";
      this.overwriteChoice = null;
      this.customFieldName = "";
      this.isCreatingCustomField = false;
    },
    selectField(fieldName) {
      this.selectedField = fieldName;
      this.isCreatingCustomField = false;
    },
    submitSelection() {
      if (this.callback && this.isValidSelection) {
        const selectedFieldData = this.fields.find(f => f.name === this.selectedField);
        
        this.callback({
          field: this.selectedField,
          value: this.editedValue.trim(),
          originalValue: this.fieldValue,
          originalField: this.descriptiveKey,
          existingValue: selectedFieldData.currentValue,
          overwrite: this.overwriteChoice
        });
      }
      this.close();
    },
  },
};
</script>

<style scoped>
.field-card {
  padding: 10px;
  margin: 5px 0;
  border: 1px solid #ccc;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  color: black;
  background-color: white;
}

.field-card:hover {
  background-color: #f5f5f5;
  border-color: #bbb;
}

.field-card.is-selected {
  background-color: #3273dc;
  color: white;
}

.field-card.has-existing-value {
  border-left: 4px solid #ffdd57;
}

.custom-field-button {
  border: 1px dashed #ccc;
  color: #666;
}

.custom-field-button:hover {
  border-color: #3273dc;
  color: #3273dc;
  background-color: #f8f9fa;
}

.custom-field-editor {
  padding: 15px;
  margin: 5px 0;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 5px;
}

.field-buttons {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.field-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.existing-value {
  font-size: 0.9em;
  color: #666;
}

.field-card.is-selected .existing-value {
  color: #eee;
}

.fields-list {
  max-height: 300px;
  overflow-y: auto;
  margin-top: 15px;
  padding: 5px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.modal-card-body .field {
  margin-bottom: 15px;
}

label.label {
  margin-bottom: 0.5rem;
}
</style>